import type { TextStyle, ViewStyle } from "react-native";

export type Stylable = TextStyle | ViewStyle;
export type StyleKey<T extends Stylable> = keyof T;
export type StyleValue<
    Type extends Stylable = Stylable,
    Key extends StyleKey<Type> = StyleKey<Type>,
> = Type[Key] extends undefined ? never : Type[Key];
export type StyleSheetKey<Key extends string, Variant extends string = ""> = Variant extends ""
    ? Key
    : `${Lowercase<Variant>}_${Key}`;
export type StyleRecord<Type extends Stylable, Style extends StyleKey<Type>, Value = Type[Style]> = Pick<Type, Style> &
    Record<Style, Value extends Type[Style] ? Value : never>;
export type PartialStyleRecord<Type extends Stylable, Style extends StyleKey<Type>, Value = Type[Style]> = Partial<
    StyleRecord<Type, Style, Value>
>;

export type StyleSheetRecord<
    Key extends string,
    Type extends Stylable = Stylable,
    Style extends StyleKey<Type> = StyleKey<Type>,
    Value extends StyleValue<Type, Style> = StyleValue<Type, Style>,
> = Record<Key, StyleRecord<Type, Style, Value>>;

export type YSingleDimensionModifier = "Top" | "Bottom";
export type YStyleModifier = YSingleDimensionModifier | "Vertical";
export type XSingleDimensionModifier = "Right" | "Left";
export type XStyleModifier = XSingleDimensionModifier | "Horizontal";
export type DimensionModifier = XStyleModifier | YStyleModifier;
export type SingleDimensionModifier = YSingleDimensionModifier | XSingleDimensionModifier;
export type MultipleDimensionModifier = Exclude<DimensionModifier, SingleDimensionModifier>;
export type PaddingStyleKey = "padding" | `padding${DimensionModifier}`;
export type MarginStyleKey = "margin" | `margin${DimensionModifier}`;
export type RadiusStyleKey = "borderRadius" | `border${YSingleDimensionModifier}${XSingleDimensionModifier}Radius`;
export type BorderWidthKey = "borderWidth" | `border${SingleDimensionModifier}Width`;
export type BorderStyleKey = "borderColor" | BorderWidthKey | RadiusStyleKey | "borderStyle";
export type OutlineStyleKey = "outlineColor" | "outlineWidth" | "outlineStyle" | "outlineOffset";
export type GapStyleKey = "gap" | "columnGap" | "rowGap";
export type PositionStyleKey = Lowercase<SingleDimensionModifier>;
export type WidthStyleKey = "width" | "maxWidth" | "minWidth";
export type HeightStyleKey = "height" | "maxHeight" | "minHeight";
export type TextStyleKey = "fontWeight" | "color" | "fontSize" | "lineHeight";

export type TextVariant =
    | "primary"
    | "secondary"
    | "tertiary"
    | "quaternary"
    | "info"
    | "success"
    | "warning"
    | "danger"
    | "selected"
    | "brand"
    | "neutral"
    | "inverse-primary"
    | "inverse-secondary"
    | "link"
    | "link-pressed"
    | "link-disabled";
export type IconVariant = TextVariant;

export type BorderVariant =
    | "primary"
    | "secondary"
    | "tertiary"
    | "quaternary"
    | "info"
    | "success"
    | "warning"
    | "danger"
    | "selected"
    | "neutral"
    | "brand";

export type BackgroundVariant =
    | "info"
    | "success"
    | "warning"
    | "danger"
    | "primary"
    | "brand"
    | "primary-pressed"
    | "secondary"
    | "secondary-pressed"
    | "tertiary"
    | "transparent"
    | "gradient-start"
    | "gradient-end";

export type InputStateVariant = "focus" | "error" | "disabled";
export type InputColourKey =
    | "background"
    | "border"
    | "text"
    | "placeholder"
    | "icon"
    | "background-disabled"
    | "text-disabled"
    | "icon-disabled"
    | `border-${InputStateVariant}`;

export type ButtonVariant =
    | "primary"
    | "secondary"
    | "quiet"
    | "quiet-inverse"
    | "neutral"
    | "neutral-quiet"
    | "destructive"
    | "destructive-quiet"
    | "success"
    | "success-quiet"
    | "warning"
    | "warning-quiet";
export type ButtonState = "pressed" | "disabled";

export type ButtonVariantColourKey =
    | ButtonVariant
    | `${ButtonVariant}-background`
    | `${ButtonVariant}-${ButtonState}`
    | `${ButtonVariant}-${ButtonState}-background`;

type BorderStyle = PartialStyleRecord<ViewStyle, BorderStyleKey>;

export type InputStyleSheet = {
    input: StyleRecord<
        ViewStyle,
        `padding${MultipleDimensionModifier}` | "height" | "outlineWidth" | "backgroundColor"
    > &
        BorderStyle;
    inputText: StyleRecord<TextStyle, TextStyleKey>;
    textArea: StyleRecord<ViewStyle, Exclude<HeightStyleKey, "maxHeight"> | "justifyContent">;
    focus: BorderStyle;
    error: BorderStyle;
    disabled: BorderStyle & StyleRecord<ViewStyle, "backgroundColor">;
    disabledText: StyleRecord<TextStyle, "color">;
    placeholder: StyleRecord<TextStyle, TextStyleKey>;
    shadow: StyleRecord<ViewStyle, "boxShadow">;
};

export type ToastColourKey = "background" | "foreground";
export type TabColourKey = "background" | "selected";

export type ToggleColourKey = "indicator" | "disabled" | "off" | "on";
export type CheckColourKey = "selector" | "background" | "disabled";
export type TrackingColourKey =
    | "indicator"
    | "info"
    | "current"
    | "prediction"
    | "selected"
    | "past-period"
    | "feature"
    | "unselected"
    | "gradient-top"
    | "gradient-bottom";

export type ColourDepth = 0 | 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

export type BaseColours = {
    transparent: "transparent";
    current: "currentColor";
    white: "#FFFFFF";
    black: "#000000";
    navy: Record<Exclude<ColourDepth, 25 | 950>, string>;
    "navy-inverse": Record<Exclude<ColourDepth, 25 | 50 | 950>, string>;
    pink: Record<Exclude<ColourDepth, 25 | 50>, string>;
    "pink-inverse": Record<Exclude<ColourDepth, 25 | 50>, string>;
    green: Record<Exclude<ColourDepth, 25>, string>;
    blue: Record<ColourDepth, string>;
    yellow: Record<Exclude<ColourDepth, 25 | 950>, string>;
    red: Record<Exclude<ColourDepth, 0 | 50 | 950>, string>;
    slate: Record<Exclude<ColourDepth, 0 | 25 | 950>, string>;
};

export type ThemeColours = {
    brand: string;
    brandHighlight: string;
    boxShadow: string;
    backdrop: string;
    toast: Record<ToastColourKey, string>;
    text: Record<TextVariant, string>;
    icon: Record<IconVariant, string>;
    border: Record<BorderVariant, string>;
    background: Record<BackgroundVariant, string>;
    button: Record<ButtonVariantColourKey, string>;
    toggle: Record<ToggleColourKey, string>;
    check: Record<CheckColourKey, string>;
    tracking: Record<TrackingColourKey, string>;
    input: Record<InputColourKey, string>;
    tabs: Record<TabColourKey, string>;
    reports: string[];
};

export type ThemedColours = BaseColours & ThemeColours;

export enum Theme {
    light_blue = "light_blue",
    dark_blue = "dark_blue",
    light_pink = "light_pink",
    dark_pink = "dark_pink",
}
export const ThemeNameMap: Record<Theme, string> = {
    [Theme.light_blue]: "Light blue",
    [Theme.dark_blue]: "Dark blue",
    [Theme.light_pink]: "Light pink",
    [Theme.dark_pink]: "Dark pink",
};
export type ThemeColourMap = Record<Theme, ThemeColours>;

export type TextColourStyle<T extends TextVariant = TextVariant> = StyleSheetRecord<
    T,
    TextStyle,
    "color",
    ThemeColours["text"][T]
>;
export type BackgroundColourStyle<T extends BackgroundVariant = BackgroundVariant> = StyleSheetRecord<
    T,
    ViewStyle,
    "backgroundColor",
    ThemeColours["background"][T]
>;
export type BorderColourStyle<T extends BorderVariant = BorderVariant> = StyleSheetRecord<
    T,
    ViewStyle,
    `borderColor`,
    ThemeColours["border"][T]
> &
    StyleSheetRecord<StyleSheetKey<T, "top">, ViewStyle, `borderTopColor`, ThemeColours["border"][T]> &
    StyleSheetRecord<StyleSheetKey<T, "right">, ViewStyle, `borderRightColor`, ThemeColours["border"][T]> &
    StyleSheetRecord<StyleSheetKey<T, "bottom">, ViewStyle, `borderBottomColor`, ThemeColours["border"][T]> &
    StyleSheetRecord<StyleSheetKey<T, "left">, ViewStyle, `borderLeftColor`, ThemeColours["border"][T]>;

export type ThemeStyles = {
    text: TextColourStyle;
    background: BackgroundColourStyle;
    border: BorderColourStyle;
};
